import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Building2, Edit3, ExternalLink, Mail, MapPin, Phone, Users, Video } from "lucide-react";
import { SOCIAL_PLATFORMS, socialHref, externalHref } from "@/lib/socials";
import { imageSrc } from "@/lib/images";

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const Row = ({ label, value }) => {
  if (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)) return null;
  const text = Array.isArray(value) ? value.join(", ") : String(value);
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground pt-0.5">{label}</span>
      <span className="break-words">{text}</span>
    </div>
  );
};

const Out = ({ href, children, testId }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testId}
     className="inline-flex items-center gap-1 text-sm text-[hsl(var(--studio-primary))] hover:underline break-all">
    {children}<ExternalLink className="h-3 w-3 shrink-0" />
  </a>
);

function PersonBody({ r }) {
  const money = (v) => (v ? `${r.currency || "AUD"} ${v}` : null);
  return (
    <div className="space-y-4">
      <section>
        <Row label="Full name" value={r.name} />
        <Row label="Preferred name" value={r.preferred_name} />
        <Row label="Pronouns" value={r.pronouns} />
        <Row label="Primary role" value={r.primary_role} />
        <Row label="Other roles" value={r.secondary_roles} />
        <Row label="Role tags" value={r.role_tags} />
        <Row label="Relationship" value={r.relationship_status} />
        <Row label="Location" value={r.location || [r.city, r.state, r.country].filter(Boolean).join(", ")} />
        <Row label="Skills" value={r.skills} />
        <Row label="Languages" value={r.languages} />
        <Row label="Tags" value={r.tags} />
        <Row label="Notes" value={r.notes} />
        <Row label="Bio" value={r.bio} />
      </section>
      {(r.day_rate || r.half_day_rate || r.weekly_rate || r.availability_notes || r.travel_willingness) && (
        <section>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Rates &amp; availability</div>
          <Row label="Day rate" value={money(r.day_rate)} />
          <Row label="Half-day rate" value={money(r.half_day_rate)} />
          <Row label="Weekly rate" value={money(r.weekly_rate)} />
          <Row label="Availability" value={r.availability_notes || r.availability} />
          <Row label="Travel" value={r.travel_willingness} />
        </section>
      )}
      {(r.agent_name || r.unions?.length || r.union_status) && (
        <section>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Representation</div>
          <Row label="Agent" value={[r.agent_name, r.agent_company].filter(Boolean).join(" · ")} />
          <Row label="Agent contact" value={[r.agent_email, r.agent_phone].filter(Boolean).join(" · ")} />
          <Row label="Unions" value={r.unions?.length ? r.unions : r.union_status} />
        </section>
      )}
      {(r.credits || []).length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Credits</div>
          {r.credits.map((c) => (
            <div key={`${c.title}-${c.year}-${c.role}`} className="text-sm py-1">
              <span className="font-medium">{c.title}</span>
              {c.role && <span className="text-muted-foreground"> — {c.role}</span>}
              {c.year && <span className="text-muted-foreground"> ({c.year})</span>}
              {c.link && <> {" "}<Out href={externalHref(c.link)}>watch</Out></>}
            </div>
          ))}
        </section>
      )}
      {(r.references || []).length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">References</div>
          {r.references.map((x) => (
            <div key={`${x.name}-${x.contact}`} className="text-sm py-1">
              {x.name}{x.role ? ` — ${x.role}` : ""}{x.contact ? ` · ${x.contact}` : ""}
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

function CompanyBody({ r, people, onOpenPerson }) {
  return (
    <div className="space-y-4">
      <section>
        <Row label="Type" value={r.type} />
        <Row label="ABN / Reg" value={r.abn} />
        <Row label="Address" value={r.address} />
        <Row label="Services" value={r.services} />
        <Row label="Industries" value={r.industries} />
        <Row label="Team size" value={r.team_size} />
        <Row label="Established" value={r.established_year} />
        <Row label="Invoice series" value={r.invoice_prefix ? `${r.invoice_prefix} — next ${r.next_invoice_number}` : null} />
        <Row label="Relationship" value={r.relationship_status} />
        <Row label="Tags" value={r.tags} />
        <Row label="Notes" value={r.notes} />
      </section>
      <section>
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
          <Users className="h-3 w-3" /> People at this company
        </div>
        {people.length === 0 && <div className="text-sm text-muted-foreground">Nobody linked yet.</div>}
        <div className="flex flex-wrap gap-1.5">
          {people.map((p) => (
            <button key={p.id} type="button" onClick={() => onOpenPerson(p.id)}
                    data-testid={`company-person-${p.id}`}
                    className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent">
              {p.name}{p.role ? ` · ${p.role}` : ""}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function ContactDetailDialog({ contact, open, onOpenChange, onEdit, onOpenPerson, onOpenCompany }) {
  if (!contact) return null;
  const r = contact.record || {};
  const isPerson = contact.kind === "person";
  const photo = imageSrc(isPerson ? r.photo_url : r.logo_url);
  const socials = contact.socials || {};
  const links = [
    ["Website", contact.website],
    ["Showreel", r.showreel_url],
    ["Resume", imageSrc(r.resume_url)],
    ...((r.portfolio_urls || []).map((u) => ["Portfolio", u])),
  ].filter(([, v]) => v);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="contact-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-start gap-3 text-left">
            {photo ? (
              <img src={photo} alt={contact.name} data-testid="contact-modal-photo"
                   className={`h-14 w-14 shrink-0 border border-border ${isPerson ? "rounded-full object-cover" : "rounded-lg object-contain bg-white/5 p-1"}`} />
            ) : isPerson ? (
              <span className="h-11 w-11 shrink-0 rounded-full bg-gradient-to-br from-[hsl(var(--studio-primary))] to-[hsl(var(--studio-secondary))] text-white text-sm font-semibold flex items-center justify-center">
                {initials(contact.name)}
              </span>
            ) : (
              <span className="h-11 w-11 shrink-0 rounded-lg border border-border bg-accent/40 flex items-center justify-center font-semibold">
                <Building2 className="h-5 w-5" />
              </span>
            )}
            <span className="min-w-0">
              <span className="block truncate">{contact.full_name || contact.name}</span>
              {contact.nickname && (
                <span className="block text-xs font-normal text-muted-foreground mt-0.5">
                  Goes by “{contact.nickname}”
                </span>
              )}
              {contact.subtitle && (
                <span className="block text-xs font-normal text-muted-foreground mt-0.5">{contact.subtitle}</span>
              )}
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pb-3 border-b border-border">
          {contact.email && <a className="flex items-center gap-1 hover:text-foreground" href={`mailto:${contact.email}`}><Mail className="w-3.5 h-3.5"/> {contact.email}</a>}
          {contact.phone && <a className="flex items-center gap-1 hover:text-foreground" href={`tel:${contact.phone}`}><Phone className="w-3.5 h-3.5"/> {contact.phone}</a>}
          {contact.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/> {contact.location}</span>}
        </div>

        {isPerson && (contact.companies || []).length > 0 && (
          <section data-testid="contact-modal-works-at">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
              <Building2 className="h-3 w-3" /> Works at
            </div>
            <div className="flex flex-wrap gap-1.5">
              {contact.companies.map((co) => (
                <button key={co.id} type="button" onClick={() => onOpenCompany?.(co.id)}
                        data-testid={`person-company-${co.id}`}
                        className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent hover:border-[hsl(var(--studio-primary)/0.45)]">
                  {co.name}{co.type ? ` · ${co.type}` : ""}
                </button>
              ))}
            </div>
          </section>
        )}

        {(contact.list_names?.length > 0 || (!isPerson && contact.company_names?.length > 0)) && (
          <div className="flex flex-wrap gap-1.5">
            {!isPerson && (contact.company_names || []).map((n) => <Badge key={n} variant="outline">{n}</Badge>)}
            {(contact.list_names || []).map((n) => <Badge key={n}>{n}</Badge>)}
          </div>
        )}

        {(Object.keys(socials).length > 0 || links.length > 0) && (
          <div className="flex flex-wrap items-center gap-2" data-testid="contact-modal-links">
            {SOCIAL_PLATFORMS.filter(({ key }) => socials[key]).map(({ key, label, icon: Icon }) => (
              <a key={key} href={socialHref(key, socials[key])} target="_blank" rel="noreferrer"
                 title={`${label}: ${socials[key]}`} data-testid={`contact-modal-social-${key}`}
                 className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:border-[hsl(var(--studio-primary)/0.45)] hover:text-[hsl(var(--studio-primary))]">
                <Icon className="h-4 w-4" />
              </a>
            ))}
            {r.showreel_url && (
              <a href={externalHref(r.showreel_url)} target="_blank" rel="noreferrer" title="Showreel"
                 className="grid h-8 w-8 place-items-center rounded-md border border-border text-muted-foreground hover:text-[hsl(var(--studio-primary))]">
                <Video className="h-4 w-4" />
              </a>
            )}
            {links.map(([label, href]) => (
              <Out key={`${label}-${href}`} href={externalHref(href)} testId={`contact-modal-link-${label.toLowerCase()}`}>{label}</Out>
            ))}
          </div>
        )}

        {isPerson
          ? <PersonBody r={r} />
          : <CompanyBody r={r} people={contact.people || []} onOpenPerson={onOpenPerson} />}

        <DialogFooter>
          <Button variant="outline" data-testid="contact-modal-edit" onClick={() => onEdit(contact)}>
            <Edit3 className="w-4 h-4 mr-2" /> Edit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
